import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarHeart,
  Clock,
  Cog,
  Eye,
  RotateCcw,
  Settings2,
  ShieldCheck,
  Sun,
  Zap,
} from "lucide-react";
import { AuthContext } from "../../Context/AuthContext";
import { useToast } from "../../Context/ToastContext";
import API from "../../../services/api";
import { InputWithIcon, SelectWithIcon } from "../../../components/ui/FieldWithIcon";
import {
  COMP_OFF_VALIDITY,
  HOLIDAY_METHODS,
  OVERTIME_WORKS_PATH,
  SUNDAY_METHODS,
  configsEqual,
  defaultExtraWorkConfig,
  extraWorkSummary,
  validatePremiumConfig,
  withDefaultPremiumSections,
} from "./extraWorkPolicy";
import "../../../styles/extraWorkConfig.css";
import { broadcastOtHoursAllocationFromExtraWork } from "../../attendance/otHoursAllocationEvents";
import { navigateToSettingsHome } from "../../../components/settings/settingsExit";

function Field({ label, required, error, hint, children, full }) {
  return (
    <div className={`ewc-field${full ? " ewc-field--full" : ""}`}>
      <label className="ewc-label">
        {label}
        {required ? <span className="ewc-req">*</span> : null}
      </label>
      {children}
      {error ? <p className="ewc-error">{error}</p> : null}
      {!error && hint ? <p className="ewc-hint">{hint}</p> : null}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label className="ewc-toggle">
      <div>
        <span className="ewc-toggle__label">{label}</span>
        {hint ? <span className="ewc-toggle__hint">{hint}</span> : null}
      </div>
      <input
        type="checkbox"
        className="ewc-check"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

function optionsFromCatalog(catalogList, fallback) {
  if (Array.isArray(catalogList) && catalogList.length) return catalogList;
  return fallback;
}

function mergeLoadedConfig(cfg) {
  return {
    ...defaultExtraWorkConfig(),
    ...cfg,
    overtime: {
      ...defaultExtraWorkConfig().overtime,
      ...(cfg.overtime || {}),
      enabled:
        typeof cfg.overtime?.enabled === "boolean"
          ? cfg.overtime.enabled
          : Boolean(cfg.processing?.ot_hours_allocation_enabled),
    },
    processing: {
      ...defaultExtraWorkConfig().processing,
      ...(cfg.processing || {}),
      ot_hours_allocation_enabled:
        typeof cfg.overtime?.enabled === "boolean"
          ? cfg.overtime.enabled
          : Boolean(cfg.processing?.ot_hours_allocation_enabled),
    },
  };
}

export default function ExtraWorkPremiumOvertimePage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user: me } = useContext(AuthContext);
  const role = (me?.role || me?.user?.role || "").toLowerCase();
  const canEdit = role === "admin" || role === "hr";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultExtraWorkConfig);
  const [saved, setSaved] = useState(defaultExtraWorkConfig);
  const [errors, setErrors] = useState({});
  const [catalog, setCatalog] = useState({});

  const dirty = !configsEqual(form, saved);
  const summary = useMemo(() => extraWorkSummary(form), [form]);
  const sundayMethods = optionsFromCatalog(catalog.sunday_compensation_methods, SUNDAY_METHODS);
  const holidayMethods = optionsFromCatalog(catalog.holiday_compensation_methods, HOLIDAY_METHODS);
  const validityPresets = catalog.comp_off_validity_presets || COMP_OFF_VALIDITY;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const cfgRes = await API.get("/organisation/me/extra-work-config");
        if (cancelled) return;
        const cfg = cfgRes.data?.extra_work || defaultExtraWorkConfig();
        const merged = mergeLoadedConfig(cfg);
        setForm(merged);
        setSaved(merged);
        broadcastOtHoursAllocationFromExtraWork(merged);
        setCatalog(cfgRes.data?.catalog || {});
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to load Premium Works configuration");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    if (!dirty) return undefined;
    function onLeave(e) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const patch = (section, updates) => {
    setForm((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...updates } }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(updates || {}).forEach((k) => {
        delete next[k];
      });
      return next;
    });
  };

  async function persist(payload, { restoreDefault = false } = {}) {
    if (!canEdit) {
      toast.error("You do not have permission to save this configuration.");
      return;
    }
    try {
      setSaving(true);
      const res = await API.put("/organisation/me/extra-work-config", {
        extra_work: payload,
        restore_default: restoreDefault,
      });
      const cfg = res.data?.extra_work || payload;
      const merged = mergeLoadedConfig(cfg);
      setForm(merged);
      setSaved(merged);
      setErrors({});
      toast.success(
        restoreDefault
          ? "Default Premium Works policy restored."
          : "Premium Works saved. Sunday / holiday / Comp-Off rules apply on the next payroll run."
      );
      broadcastOtHoursAllocationFromExtraWork(merged);
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0] || "Please correct the highlighted fields");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save Premium Works configuration");
      }
    } finally {
      setSaving(false);
    }
  }

  function onSave() {
    const nextErrors = validatePremiumConfig(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error("Please correct the highlighted fields before saving.");
      return;
    }
    persist(form);
  }

  function onCancel() {
    if (dirty && !window.confirm("Discard unsaved Premium Works changes?")) return;
    navigate("/settings");
  }

  function onReset() {
    if (!window.confirm("Reset this page to the last saved Premium Works policy?")) return;
    setForm(saved);
    setErrors({});
  }

  function onRestoreDefault() {
    if (!window.confirm("Restore default Premium Works settings? Overtime Works settings will stay unchanged.")) return;
    persist(withDefaultPremiumSections(saved), { restoreDefault: false });
  }

  const disabled = !canEdit || loading || saving;
  const prem = form.premium || {};
  const co = form.comp_off || {};
  const proc = form.processing || {};

  return (
    <div className="ewc-root">
      <p className="ewc-lead">
        Configure Sunday work, holiday work, premium multipliers, and Comp-Off. Overtime enable / hours
        source / pay calculation live on <Link to={OVERTIME_WORKS_PATH}>Overtime Works</Link>.
      </p>

      {dirty ? (
        <div className="ewc-warn" role="status">
          You have unsaved Premium Works policy changes.
        </div>
      ) : null}

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-500">Loading Premium Works configuration…</p>
      ) : (
        <div className="ewc-page">
          <div className="ewc-stack">
            <section className="ewc-card">
              <div className="ewc-card__head">
                <Sun className="ewc-card__icon text-amber-500" aria-hidden />
                <div>
                  <h3>Sunday Work Treatment</h3>
                  <p>How payroll compensates employees who work on the weekly off (typically Sunday).</p>
                </div>
              </div>
              <p className="ewc-example">
                The selected method is applied automatically to Sunday attendance during payroll generation.
                If the method is overtime pay, those hours are added to the Overtime component — they are not a second extra-time earning.
              </p>
              <Field label="Sunday Work Compensation Method" required error={errors.sunday_method}>
                <SelectWithIcon
                  icon={Sun}
                  value={form.sunday?.compensation_method || ""}
                  disabled={disabled}
                  onChange={(e) => patch("sunday", { compensation_method: e.target.value })}
                >
                  <option value="">Select a method</option>
                  {sundayMethods.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </SelectWithIcon>
              </Field>
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <CalendarHeart className="ewc-card__icon text-rose-500" aria-hidden />
                <div>
                  <h3>Holiday Work Treatment</h3>
                  <p>Independent of Sunday work — organisations often use a different holiday rule.</p>
                </div>
              </div>
              <p className="ewc-example">
                Applied when an employee is present on an official organisation holiday.
                Overtime-pay methods fold into the same Overtime earning; double salary stays as extra payable days.
              </p>
              <Field label="Holiday Work Compensation Method" required error={errors.holiday_method}>
                <SelectWithIcon
                  icon={CalendarHeart}
                  value={form.holiday?.compensation_method || ""}
                  disabled={disabled}
                  onChange={(e) => patch("holiday", { compensation_method: e.target.value })}
                >
                  <option value="">Select a method</option>
                  {holidayMethods.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </SelectWithIcon>
              </Field>
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <Zap className="ewc-card__icon text-violet-600" aria-hidden />
                <div>
                  <h3>Premium Pay Rules</h3>
                  <p>Multipliers used when a custom policy is selected, and for night / weekend / festival premiums.</p>
                </div>
              </div>
              <div className="ewc-grid">
                {[
                  ["sunday_multiplier", "Sunday Premium Multiplier"],
                  ["holiday_multiplier", "Holiday Premium Multiplier"],
                  ["night_shift_multiplier", "Night Shift Premium"],
                  ["weekend_multiplier", "Weekend Premium"],
                  ["special_multiplier", "Special Premium"],
                  ["festival_multiplier", "Festival Premium"],
                ].map(([key, label]) => (
                  <Field key={key} label={label} required error={errors[key]} hint="Example: 1.0x, 1.5x, 2.0x">
                    <InputWithIcon
                      icon={Zap}
                      type="number"
                      min={0.1}
                      max={5}
                      step={0.1}
                      value={prem[key] ?? 1}
                      disabled={disabled}
                      onChange={(e) => patch("premium", { [key]: e.target.value })}
                    />
                  </Field>
                ))}
              </div>
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <CalendarHeart className="ewc-card__icon text-cyan-600" aria-hidden />
                <div>
                  <h3>Compensatory Off Configuration</h3>
                  <p>Grant, validity, expiry, approval, and automatic credit / expiry of Comp-Off.</p>
                </div>
              </div>
              <div className="ewc-toggles">
                <Toggle
                  label="Grant Comp-Off"
                  hint="Credit compensatory off when the Sunday or holiday method includes Comp-Off"
                  checked={co.grant_enabled}
                  disabled={disabled}
                  onChange={(v) => patch("comp_off", { grant_enabled: v })}
                />
              </div>
              <div className="ewc-grid" style={{ marginTop: 8 }}>
                <Field label="Comp-Off Validity" required error={errors.validity_days}>
                  <SelectWithIcon
                    icon={Clock}
                    value={String(co.validity_days || 90)}
                    disabled={disabled || !co.grant_enabled}
                    onChange={(e) => patch("comp_off", { validity_days: Number(e.target.value) })}
                  >
                    {validityPresets.map((d) => (
                      <option key={d} value={d}>{d} Days</option>
                    ))}
                  </SelectWithIcon>
                </Field>
                <Field label="Expiry Policy">
                  <SelectWithIcon
                    icon={Clock}
                    value={co.carry_forward_allowed ? "carry_forward" : "lapse"}
                    disabled={disabled || !co.grant_enabled}
                    onChange={(e) =>
                      patch("comp_off", {
                        expiry_policy: e.target.value,
                        carry_forward_allowed: e.target.value === "carry_forward",
                      })
                    }
                  >
                    <option value="lapse">Expire after validity (no carry forward)</option>
                    <option value="carry_forward">Carry Forward Allowed</option>
                  </SelectWithIcon>
                </Field>
                <Field label="Maximum Balance">
                  <InputWithIcon
                    icon={Settings2}
                    type="number"
                    min={0}
                    value={co.max_balance ?? 12}
                    disabled={disabled || !co.grant_enabled}
                    onChange={(e) => patch("comp_off", { max_balance: e.target.value })}
                  />
                </Field>
              </div>
              <div className="ewc-toggles">
                <Toggle
                  label="Approval Required"
                  checked={co.approval_required}
                  disabled={disabled || !co.grant_enabled}
                  onChange={(v) => patch("comp_off", { approval_required: v })}
                />
                <Toggle
                  label="Manager Approval"
                  checked={co.manager_approval}
                  disabled={disabled || !co.grant_enabled}
                  onChange={(v) => patch("comp_off", { manager_approval: v })}
                />
                <Toggle
                  label="HR Approval"
                  checked={co.hr_approval}
                  disabled={disabled || !co.grant_enabled}
                  onChange={(v) => patch("comp_off", { hr_approval: v })}
                />
                <Toggle
                  label="Automatic Credit"
                  hint="Payroll credits Comp-Off when the selected work method includes it"
                  checked={co.automatic_credit}
                  disabled={disabled || !co.grant_enabled}
                  onChange={(v) => patch("comp_off", { automatic_credit: v })}
                />
                <Toggle
                  label="Automatic Expiry"
                  checked={co.automatic_expiry}
                  disabled={disabled || !co.grant_enabled}
                  onChange={(v) => patch("comp_off", { automatic_expiry: v })}
                />
              </div>
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <ShieldCheck className="ewc-card__icon text-emerald-600" aria-hidden />
                <div>
                  <h3>Premium Payroll Processing</h3>
                  <p>How Sunday / holiday / week-off premium enters salary processing.</p>
                </div>
              </div>
              <div className="ewc-toggles">
                <Toggle
                  label="Pay over-duty days in salary"
                  hint="Days worked above the monthly payable limit are added to payable days"
                  checked={proc.over_duty_pay_enabled}
                  disabled={disabled}
                  onChange={(v) => patch("processing", { over_duty_pay_enabled: v })}
                />
                <Toggle
                  label="Also apply premium on configured week-off days"
                  checked={proc.weekoff_work_premium_enabled}
                  disabled={disabled}
                  onChange={(v) => patch("processing", { weekoff_work_premium_enabled: v })}
                />
                <Toggle
                  label="Require HR approval before paying Sunday / holiday premium"
                  checked={proc.require_hr_approval}
                  disabled={disabled}
                  onChange={(v) => patch("processing", { require_hr_approval: v })}
                />
              </div>
              <ul className="ewc-engine" style={{ marginTop: 12 }}>
                <li>Read Sunday and Holiday work policies</li>
                <li>Apply premium multipliers from this page</li>
                <li>Keep double-salary extra days as extra payable days — not a second overtime line</li>
                <li>Generate Comp-Off units where configured</li>
                <li>
                  Overtime pay methods that fold into OVERTIME use{" "}
                  <Link to={OVERTIME_WORKS_PATH}>Overtime Works</Link> calculation settings
                </li>
              </ul>
            </section>
          </div>

          <aside className="ewc-summary" aria-label="Preview premium configuration summary">
            <h3><Eye size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Preview Summary</h3>
            <dl>
              <div><dt>Sunday Work</dt><dd>{summary.sunday}</dd></div>
              <div><dt>Holiday Work</dt><dd>{summary.holiday}</dd></div>
              <div><dt>Sunday Multiplier</dt><dd>{summary.sundayMultiplier}</dd></div>
              <div><dt>Holiday Multiplier</dt><dd>{summary.holidayMultiplier}</dd></div>
              <div><dt>Comp-Off</dt><dd>{summary.compOff}</dd></div>
              <div><dt>Validity</dt><dd>{summary.validity}</dd></div>
              <div><dt>HR Approval</dt><dd>{summary.hrApproval}</dd></div>
            </dl>
            {dirty ? (
              <p className="ewc-hint" style={{ marginTop: 10 }}>
                <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
                Unsaved — review before leaving this page.
              </p>
            ) : null}
          </aside>
        </div>
      )}

      {canEdit && !loading ? (
        <div className="ewc-footer-bar" role="toolbar" aria-label="Premium Works actions">
          <button type="button" className="ewc-btn ewc-btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <span className="ewc-footer-bar__spacer" />
          <button type="button" className="ewc-btn ewc-btn--ghost" onClick={onReset} disabled={saving || !dirty}>
            <RotateCcw size={14} /> Reset
          </button>
          <button type="button" className="ewc-btn ewc-btn--danger" onClick={onRestoreDefault} disabled={saving}>
            Restore Default
          </button>
          <button type="button" className="ewc-btn ewc-btn--primary" onClick={onSave} disabled={saving || loading}>
            <Cog size={15} /> {saving ? "Saving…" : "Save Configuration"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
